/* eslint-disable max-lines */
import type React from "react";
import { useLayoutEffect, useState } from "react";

import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { colorsSelector } from "@redux/colorPalette/selectors";
import {
	publicInteractionIsWaitingSongActivatedSelector,
	selectedVocalEffectSelector,
} from "@redux/featureFlags/selector";
import {
	useDisableFeatureSelector,
	useMusicPitchValueSelector,
	playNextSong,
	saveCurrentSong,
	useRemoveFromWaitingList,
	useWaitingListSelector,
} from "@redux/player";
import localDB from "lib/IndexedDB";
import analytics from "lib/analytics";
import { useDispatch, useSelector } from "react-redux";
import { useBoolean } from "react-use";

import type ControlBarContent from "components/b2b/ControlBar/ControlBarContent";
import { getVocalGuideButtonState } from "components/b2b/ControlBar/ControlBarContent/ControlBarContent.utils";
import type CastingControlBarContent from "components/b2c/CastingControlBar/components/CastingControlBarContent";
import client from "services/networking/client";
import {
	PlayerEvent,
	type PlayerEventsListeners,
	type SendPlayerEvent,
} from "shared-types/player-event";
import type { SongWithSinger } from "shared-types/song";

import {
	getTimeSliderValue,
	getTimeSliderMax,
	getRemainingTime,
} from "./ControlBarEventsWrapper.utils";
import { AudioOptionButtonState } from "../../b2b/ControlBar/ControlBarContent/VoiceControl/AudioOptionButton/AudioOptionButton.const";
import { useVocalGuideState } from "services/hooks/useVocalGuideState";

interface Props {
	ControlBarContentComponent:
		| typeof ControlBarContent
		| typeof CastingControlBarContent;
	sendPlayerEvent: SendPlayerEvent;
	usePlayerEventsListener: (
		eventListeners: PlayerEventsListeners,
		castingSessionId?: string,
	) => void;
	currentSong: {
		id: string;
	} | null;
	nextSong: SongWithSinger | null;
	controlsDisabled?: boolean;
	songTitle: string | undefined;
	songArtistName: string | undefined;
	castingSessionId?: string;
	isVocalGuideAvailable?: boolean;
}

const ControlBarEventsWrapper: React.FC<Props> = ({
	ControlBarContentComponent,
	sendPlayerEvent,
	usePlayerEventsListener,
	currentSong,
	nextSong,
	controlsDisabled = false,
	songTitle,
	songArtistName,
	castingSessionId,
	isVocalGuideAvailable = false,
}) => {
	const dispatch = useDispatch();
	const [isPlaying, setIsPlaying] = useBoolean(false);
	const [videoDuration, setVideoDuration] = useState<number | null>(0);
	const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
	const [videoFutureTime, setVideoFutureTime] = useState<number | null>(null);
	const waitingList = useWaitingListSelector();
	const isWaitingSongActivated = useSelector(
		publicInteractionIsWaitingSongActivatedSelector,
	);
	const [waitingSongCount, setWaitingSongCount] = useState(0);
	const colors = useSelector(colorsSelector);
	const disableFeatures = useDisableFeatureSelector();
	const { removeFromWaitingList } = useRemoveFromWaitingList();
	const selectedEffect = useSelector(selectedVocalEffectSelector);
	const musicPicthValue = useMusicPitchValueSelector();
	const { isVocalGuideActivated, deactivateVocalGuide, activateVocalGuide } =
		useVocalGuideState(null, null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useLayoutEffect(() => {
		setVideoDuration(null);
		sendPlayerEvent(PlayerEvent.SELECT_SONG, currentSong?.id);
		analytics.track("player_song_change", {
			songId: currentSong?.id,
			name: songTitle,
			selectedVoiceEffect: selectedEffect,
			selectPitchValue: musicPicthValue,
		});
	}, [currentSong]);

	usePlayerEventsListener(
		{
			[PlayerEvent.ON_PLAY]: () => {
				setIsPlaying(true);
			},
			[PlayerEvent.ON_PAUSE]: () => {
				setIsPlaying(false);
			},
			[PlayerEvent.DURATION]: (event: MessageEvent) => {
				// When duration changes, it means that song changed so we reset currentTime
				setVideoCurrentTime(0);
				const duration = Number.parseInt(event.data, 10);
				setVideoDuration(Number.isNaN(duration) ? null : duration);
			},
			[PlayerEvent.CURRENT_TIME]: (event: MessageEvent) => {
				const currentTime = Number.parseInt(event.data, 10);
				setVideoCurrentTime(Number.isNaN(currentTime) ? 0 : currentTime);
			},
			[PlayerEvent.SONG_END]: () => {
				handlePlayNextSongClick();
			},
			[PlayerEvent.ON_MUTE_VOCAL_GUIDE]: () => {
				deactivateVocalGuide();
			},
			[PlayerEvent.ON_UNMUTE_VOCAL_GUIDE]: () => {
				activateVocalGuide();
			},
		},
		castingSessionId,
	);

	const handleRestartSongClick = () => {
		sendPlayerEvent(PlayerEvent.SET_TIME, "0");
		analytics.track("player_previous");
	};

	const handlePlayPauseClick = () => {
		if (!currentSong && waitingList.length) {
			dispatch(saveCurrentSong(waitingList[0]));
			removeFromWaitingList(0);
		}

		sendPlayerEvent(isPlaying ? PlayerEvent.PAUSE : PlayerEvent.PLAY);
	};

	const handlePlayNextSongClick = async () => {
		if (isWaitingSongActivated) {
			const waitingSongs = await localDB.fetchOrGetData<SongWithSinger[]>(
				"/songs/waitingSongs",
				(url: string) => client.post(url, {}),
			);
			const isCurrentSongIsWaitingSong = waitingSongs.some(
				(waitingSong) => waitingSong.id === currentSong?.id,
			);
			if (!isCurrentSongIsWaitingSong) {
				const waitingSong =
					waitingSongs[waitingSongCount % waitingSongs.length];
				waitingSong.singerName = nextSong?.singerName;

				setWaitingSongCount(waitingSongCount + 1);
				return dispatch(saveCurrentSong(waitingSong));
			}
		}

		setVideoCurrentTime(0);
		dispatch(playNextSong());
	};

	const handleTimeSliderChange = (value: number | number[]) => {
		setVideoFutureTime(value as number);
	};

	const handleTimeSliderChangeCommitted = () => {
		sendPlayerEvent(PlayerEvent.SET_TIME, videoFutureTime?.toString());
		if (videoFutureTime !== null) {
			setVideoCurrentTime(videoFutureTime);
			analytics.track("player_slider_change", {
				value: videoFutureTime,
				songId: currentSong?.id,
				name: songTitle,
			});
		}
		setVideoFutureTime(null);
	};

	const handleToggleVocalGuideClick = (buttonState: AudioOptionButtonState) => {
		if (buttonState === AudioOptionButtonState.DISABLED) {
			return;
		}

		sendPlayerEvent(
			buttonState === AudioOptionButtonState.ACTIVE
				? PlayerEvent.MUTE_VOCAL_GUIDE
				: PlayerEvent.UNMUTE_VOCAL_GUIDE,
		);
	};

	const PlayPauseIcon = isPlaying ? PauseIcon : PlayArrowRoundedIcon;

	const vocalGuideButtonState = getVocalGuideButtonState(
		controlsDisabled,
		isVocalGuideAvailable,
		isVocalGuideActivated,
	);

	return (
		<ControlBarContentComponent
			controlsDisabled={controlsDisabled || disableFeatures}
			vocalGuideButtonState={vocalGuideButtonState}
			PlayPauseIcon={PlayPauseIcon}
			handleRestartSongClick={handleRestartSongClick}
			handlePlayPauseClick={handlePlayPauseClick}
			handlePlayNextSongClick={handlePlayNextSongClick}
			handleTimeSliderChange={handleTimeSliderChange}
			handleTimeSliderChangeCommitted={handleTimeSliderChangeCommitted}
			handleToggleVocalGuideClick={() =>
				handleToggleVocalGuideClick(vocalGuideButtonState)
			}
			nextSong={nextSong}
			songTitle={songTitle}
			songArtistName={songArtistName}
			timeSliderValue={getTimeSliderValue(
				controlsDisabled,
				videoFutureTime,
				videoCurrentTime,
				videoDuration,
			)}
			timeSliderMax={getTimeSliderMax(controlsDisabled, videoDuration)}
			remainingTime={getRemainingTime(videoDuration, videoCurrentTime)}
			colors={colors}
		/>
	);
};

export default ControlBarEventsWrapper;
